import { grey, red } from "@mui/material/colors";
import { createTheme, responsiveFontSizes } from "@mui/material/styles";

import { getBrand } from "./config/product";
import { dataGridNoFocusOutlineSx } from "./utils/dataGridSx";
// Types `components.MuiDataGrid` below (Pro 6.9 reads the same key at runtime).
import type {} from "@mui/x-data-grid-premium/themeAugmentation";

const defaultTheme = createTheme();

/**
 * Accent the MUI theme is built from.
 *
 * `product.ts` already resolves a per-tenant brand at runtime and carries the
 * right hex in `BRANDS[brand].accent`, but nothing consumed it for the MUI
 * layer: `index.tsx` picks the theme from the build-time REACT_APP_NEW_THEME
 * flag, which is `true` everywhere, so every tenant rendered Aeronet teal —
 * G2 included, despite `BRANDS.g2.theme: "blue"`.
 *
 * Applied per-brand: BRANDS.aeronet.accent IS the teal, so the five product
 * tenants are unchanged; aquipe gets its signed-off #C0551E orange (it shipped
 * orange mid-week 2026-08 via orangeTheme before the develop-align dropped it —
 * see the aquipe brand decision, rebrand = new colours) and g2 its blue.
 */
const ACCENT = getBrand().accent;

// Exposed to plain CSS (see utils/global.css `.detail-view-tabs`) so stylesheet
// rules can carry the tenant accent without duplicating the hex per brand.
if (typeof document !== "undefined") {
  document.documentElement.style.setProperty("--aeronet-accent", ACCENT);
}

let blueTheme = createTheme({
  palette: {
    primary: {
      main: "#00afe9",
      contrastText: "#fff",
    },
    secondary: {
      main: "#767676",
      contrastText: "#fff",
    },
    error: {
      main: red.A400,
    },
    background: {
      default: "#fff",
    },
    text: {
      primary: grey[700],
      secondary: grey[500],
      disabled: grey[400],
    },
  },
  components: {
    // Every MUI X grid, Pro and Premium, wrapper or module-local (H95).
    MuiDataGrid: {
      styleOverrides: {
        root: dataGridNoFocusOutlineSx,
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
      },
    },
    MuiTypography: {
      defaultProps: {
        align: "left",
        variantMapping: {
          h6: "h1",
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small" },
    },
    MuiFormControl: {
      defaultProps: { fullWidth: true, margin: "dense", size: "small" },
    },
    MuiLink: {
      defaultProps: { underline: "none" },
    },
    MuiTable: {
      defaultProps: { size: "small" },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: "0.875rem",
          lineHeight: 1.43,
          letterSpacing: "0.01071em",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        gutters: {
          paddingLeft: defaultTheme.spacing(2),
          paddingRight: defaultTheme.spacing(2),
          [defaultTheme.breakpoints.up("sm")]: {
            paddingLeft: defaultTheme.spacing(6),
            paddingRight: defaultTheme.spacing(6),
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          minWidth: "160px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: defaultTheme.spacing(2),
          paddingBottom: defaultTheme.spacing(2),
          [defaultTheme.breakpoints.down("md")]: {
            "&:first-child": {
              paddingLeft: defaultTheme.spacing(1) + "!important;",
            },
            "&:last-child": {
              paddingRight: defaultTheme.spacing(1) + "!important;",
            },
          },
          [defaultTheme.breakpoints.up("sm")]: {
            "&:first-child": {
              paddingLeft: defaultTheme.spacing(6),
            },
            "&:last-child": {
              paddingRight: defaultTheme.spacing(6),
            },
          },
        },
        head: {
          color: grey[600],
        },
      },
    },
  },
  typography: {
    h1: {
      color: "#00afe9",
      fontSize: "2rem",
      fontWeight: 300,
    },
    h2: {
      color: "#00afe9",
      fontSize: "1.67rem",
      fontWeight: 300,
    },
    h3: {
      color: "#00afe9",
      fontSize: "1.5rem",
      fontWeight: 300,
    },
    h4: {
      color: grey[600],
      fontSize: "1rem",
      fontWeight: 500,
    },
    h5: {
      color: grey[600],
      fontSize: "1rem",
      fontWeight: 500,
    },
    h6: {
      color: "#00afe9",
      fontSize: "0.9rem",
    },
  },
  mixins: {
    toolbar: {
      // Match Aeronet Brain `layout.topBarHeight` (70)
      minHeight: 70,
      [defaultTheme.breakpoints.up("sm")]: {
        minHeight: 70,
      },
    },
  },
});
blueTheme = responsiveFontSizes(blueTheme);

let tealTheme = createTheme({
  palette: {
    primary: {
      main: ACCENT,
      contrastText: "#fff",
    },
    secondary: {
      main: "#767676",
      contrastText: "#fff",
    },
    error: {
      main: red.A400,
    },
    background: {
      default: "#fff",
    },
    text: {
      primary: grey[700],
      secondary: grey[500],
      disabled: grey[400],
    },
  },
  components: {
    // Every MUI X grid, Pro and Premium, wrapper or module-local (H95).
    MuiDataGrid: {
      styleOverrides: {
        root: dataGridNoFocusOutlineSx,
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
      },
    },
    MuiTypography: {
      defaultProps: {
        align: "left",
        variantMapping: {
          h6: "h1",
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small" },
    },
    MuiFormControl: {
      defaultProps: { fullWidth: true, margin: "normal", size: "small" },
    },
    MuiLink: {
      defaultProps: { underline: "none" },
    },
    MuiTable: {
      defaultProps: { size: "small" },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: "0.875rem",
          lineHeight: 1.43,
          letterSpacing: "0.01071em",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        gutters: {
          paddingLeft: defaultTheme.spacing(2),
          paddingRight: defaultTheme.spacing(2),
          [defaultTheme.breakpoints.up("sm")]: {
            paddingLeft: defaultTheme.spacing(6),
            paddingRight: defaultTheme.spacing(6),
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          minWidth: "160px",
          color: "black",
          fontWeight: "bold",
          "&:hover": {
            color: ACCENT,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: defaultTheme.spacing(2),
          paddingBottom: defaultTheme.spacing(2),
          [defaultTheme.breakpoints.down("md")]: {
            "&:first-child": {
              paddingLeft: 0 + "!important;",
            },
            "&:last-child": {
              paddingRight: 0 + "!important;",
            },
          },
          [defaultTheme.breakpoints.up("sm")]: {
            "&:first-child": {
              paddingLeft: 0,
            },
            "&:last-child": {
              paddingRight: 0,
            },
          },
        },
        head: {
          color: grey[600],
        },
      },
    },
  },
  typography: {
    h1: {
      color: ACCENT,
      fontSize: "2rem",
      fontWeight: 300,
    },
    h2: {
      color: ACCENT,
      fontSize: "1.67rem",
      fontWeight: 300,
    },
    h3: {
      color: ACCENT,
      fontSize: "1.5rem",
      fontWeight: 300,
    },
    h4: {
      color: grey[600],
      fontSize: "1rem",
      fontWeight: 500,
    },
    h5: {
      color: grey[600],
      fontSize: "1rem",
      fontWeight: 500,
    },
    h6: {
      color: ACCENT,
      fontSize: "0.9rem",
    },
  },
  mixins: {
    toolbar: {
      // Match Aeronet Brain `layout.topBarHeight` (70)
      minHeight: 70,
      [defaultTheme.breakpoints.up("sm")]: {
        minHeight: 70,
      },
    },
  },
});
tealTheme = responsiveFontSizes(tealTheme);

export { blueTheme, tealTheme };